import React, { useState } from "react";
import DirectionsCarFilledOutlinedIcon from "@mui/icons-material/DirectionsCarFilledOutlined";
import TwoWheelerIcon from "@mui/icons-material/TwoWheeler";
import DirectionsBusIcon from "@mui/icons-material/DirectionsBus";
import TrainIcon from "@mui/icons-material/Train";
import DirectionsWalkIcon from "@mui/icons-material/DirectionsWalk";
import DirectionsBikeIcon from "@mui/icons-material/DirectionsBike";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { CircularProgress } from "@mui/material";
import DialogLayout from "../../../Components/DialogLayout";
import { BrandContent, useTailwindBrand } from "../../../Components/shared";
import { getActiveClientBrandConfig, getActivePrestationBrandConfig } from "../../../config/environment.config";

const MODE_ICONS: Record<string, React.ReactNode> = {
    car: <DirectionsCarFilledOutlinedIcon />,
    motorcycle: <TwoWheelerIcon />,
    public_transport: <DirectionsBusIcon />,
    train: <TrainIcon />,
    foot: <DirectionsWalkIcon />,
    bike: <DirectionsBikeIcon />,
    other: <MoreHorizIcon />,
};

interface Props {
    open: boolean;
    clientName: string;
    /** Libellés renvoyés par /travel-info (travelModes). */
    travelModes: Record<string, string>;
    onSelect: (mode: string) => Promise<void> | void;
    onSkip: () => void;
}

/**
 * P7 : question légère posée une seule fois, au premier cours déclaré pour un
 * client dont le mode de déplacement n'est pas connu. Jamais bloquante : le
 * prof peut passer, et corriger plus tard depuis la fiche client.
 */
const TravelModeQuestion = ({ open, clientName, travelModes, onSelect, onSkip }: Props) => {
    const { classes, common } = useTailwindBrand();
    const [selecting, setSelecting] = useState<string | null>(null);

    const handleSelect = async (mode: string) => {
        if (selecting) return;
        setSelecting(mode);
        try {
            await onSelect(mode);
        } finally {
            setSelecting(null);
        }
    };

    return (
        <DialogLayout
            title={`Comment vous rendez-vous chez ${clientName || getActiveClientBrandConfig("", "cet ", "ce ")} ?`}
            open={open}
            handleClose={onSkip}
            handleCloseIcon={onSkip}
            customDialogActions="hidden"
            maxWidth="sm"
            text={
                <div className={`${common.body} space-y-4`}>
                    <BrandContent
                        tag="p"
                        type="description"
                        children={<>
                            Indiquez votre mode de déplacement habituel pour vos {getActivePrestationBrandConfig("s")} chez {getActiveClientBrandConfig("", "cet ", "ce ")}.
                            Cette information n'est demandée qu'une fois et reste modifiable depuis sa fiche.
                        </>}
                    />
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                        {Object.entries(travelModes ?? {}).map(([value, label]) => (
                            <button
                                key={value}
                                type="button"
                                disabled={selecting !== null}
                                onClick={() => handleSelect(value)}
                                className={`flex flex-col items-center justify-center gap-1 rounded-lg border px-2 py-3 text-sm transition hover:bg-gray-50 disabled:opacity-60 ${classes.text} ${selecting === value ? "border-gray-400 bg-gray-50" : "border-gray-200"}`}
                            >
                                <span className={classes.primaryText}>
                                    {selecting === value ? <CircularProgress size={22} /> : (MODE_ICONS[value] ?? <MoreHorizIcon />)}
                                </span>
                                <span className="font-medium">{label}</span>
                            </button>
                        ))}
                    </div>
                    <div className="flex justify-end">
                        <button
                            type="button"
                            disabled={selecting !== null}
                            onClick={onSkip}
                            className={`text-sm underline ${classes.primaryText} disabled:opacity-60`}
                        >
                            Passer pour l'instant
                        </button>
                    </div>
                </div>
            }
        />
    );
};

export default TravelModeQuestion;
